import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Brand } from "../Brand";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { LogIn, ShieldCheck, KeyRound, ArrowLeft } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, twoFactorChallenge } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  // 2FA step-2 state
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);

  const finish = (u) => {
    toast.success("Welcome back");
    // If the user hasn't enrolled 2FA yet, the backend has issued a scoped
    // enrolment-only token — sending them anywhere else just produces 403s on
    // every admin/customer API. Route them directly to the settings page
    // where the 2FA enrolment widget will auto-open via ?enroll=1.
    if (u?.must_enroll_2fa) {
      navigate("/account/settings?enroll=1", { replace: true });
      return;
    }
    const next = new URLSearchParams(location.search).get("next")
                  || (u.is_staff ? "/admin/orders" : "/account");
    navigate(next, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(form.email, form.password);
      if (result?.requires_2fa) {
        setChallenge(result.challenge);
        toast.info("Enter your 2FA code");
      } else {
        finish(result);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally { setLoading(false); }
  };

  const submitTotp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await twoFactorChallenge(challenge, code, useRecovery, rememberDevice);
      finish(u);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally { setLoading(false); }
  };

  return (
    <div
      className="min-h-screen xch-app-bg flex items-start sm:items-center justify-center p-4 py-6 sm:py-10 overflow-y-auto"
      style={{ minHeight: "100dvh", paddingBottom: "max(env(safe-area-inset-bottom), 6rem)" }}
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        <button type="button" onClick={() => navigate("/")} className="block mx-auto mb-6" data-testid="login-back-home">
          <Brand size={28} />
        </button>
        <div className="xch-card p-7 space-y-5" data-testid="login-card">
          {!challenge ? (
            <>
              <div className="text-center space-y-1.5">
                <div className="xch-eyebrow"><LogIn className="w-3 h-3" /> Welcome back</div>
                <h1 className="xch-headline text-3xl">Sign in</h1>
                <p className="text-sm text-muted-foreground">Access your orders, wallets and verification status.</p>
              </div>
              <form onSubmit={submit} className="space-y-3">
                <Field label="Email">
                  <Input required type="email" value={form.email} autoComplete="email"
                         onChange={(e) => setForm({ ...form, email: e.target.value })}
                         className="rounded-xl" data-testid="login-email-input" />
                </Field>
                <Field label="Password">
                  <Input required type="password" value={form.password} autoComplete="current-password"
                         onChange={(e) => setForm({ ...form, password: e.target.value })}
                         className="rounded-xl" data-testid="login-password-input" />
                </Field>
                <button type="button"
                        onClick={() => navigate("/forgot-password")}
                        className="text-xs text-primary hover:underline block ml-auto"
                        data-testid="login-forgot-btn">Forgot password?</button>
                <Button type="submit" disabled={loading}
                        className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl h-12 font-display font-semibold"
                        data-testid="login-submit-btn">
                  {loading ? "Signing in…" : "Sign in"}
                </Button>
              </form>
              <div className="text-center text-sm text-muted-foreground">
                New to Xchangely?{" "}
                <Link to="/register" className="text-primary font-semibold hover:underline" data-testid="login-register-link">
                  Create an account
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="text-center space-y-1.5">
                <div className="xch-eyebrow"><ShieldCheck className="w-3 h-3" /> Two-factor authentication</div>
                <h1 className="xch-headline text-3xl">{useRecovery ? "Use a recovery code" : "Enter your 2FA code"}</h1>
                <p className="text-sm text-muted-foreground">
                  {useRecovery
                    ? "Paste one of the 10 recovery codes you saved during enrollment."
                    : "Open your authenticator app and enter the 6-digit code."}
                </p>
              </div>
              <form onSubmit={submitTotp} className="space-y-3">
                <Field label={useRecovery ? "Recovery code" : "6-digit code"}>
                  <Input required value={code}
                         maxLength={useRecovery ? 14 : 6}
                         autoComplete="one-time-code"
                         inputMode={useRecovery ? "text" : "numeric"}
                         placeholder={useRecovery ? "xxxxxxxxxxxx" : "123456"}
                         onChange={(e) => setCode(e.target.value.trim())}
                         className="rounded-xl text-center text-xl font-mono tracking-[0.4em] h-14"
                         data-testid="login-2fa-code-input" />
                </Field>
                <Button type="submit" disabled={loading || code.length < (useRecovery ? 6 : 6)}
                        className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl h-12 font-display font-semibold gap-2"
                        data-testid="login-2fa-submit-btn">
                  <ShieldCheck className="w-4 h-4" />
                  {loading ? "Verifying…" : "Verify and sign in"}
                </Button>
                {!useRecovery && (
                  <label className="flex items-center gap-2 cursor-pointer text-xs text-foreground bg-muted/30 rounded-xl p-2.5"
                          data-testid="login-2fa-remember-label">
                    <input type="checkbox"
                            checked={rememberDevice}
                            onChange={(e) => setRememberDevice(e.target.checked)}
                            className="w-4 h-4 rounded border-border accent-primary"
                            data-testid="login-2fa-remember-checkbox" />
                    <span>Remember this browser for 30 days <span className="text-muted-foreground">(skip 2FA next time)</span></span>
                  </label>
                )}
                <div className="flex items-center justify-between text-xs">
                  <button type="button" onClick={() => { setChallenge(null); setCode(""); setUseRecovery(false); }}
                          className="text-muted-foreground hover:text-foreground flex items-center gap-1"
                          data-testid="login-2fa-back-btn">
                    <ArrowLeft className="w-3 h-3" /> Back to sign in
                  </button>
                  <button type="button" onClick={() => { setUseRecovery(!useRecovery); setCode(""); }}
                          className="text-primary hover:underline flex items-center gap-1"
                          data-testid="login-2fa-recovery-toggle">
                    <KeyRound className="w-3 h-3" /> {useRecovery ? "Use authenticator instead" : "Use a recovery code"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);
